import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Footer } from '@/components/layout/footer'
import { PageShell } from '@/components/layout/PageShell'
import { P2pBuyPanel } from '@/components/p2p/detail/P2pBuyPanel'
import { P2pMarketOutcomes } from '@/components/p2p/detail/P2pMarketOutcomes'
import { P2pMarketDescription } from '@/components/p2p/detail/P2pMarketDescription'
import { P2pMarketHeader } from '@/components/p2p/detail/P2pMarketHeader'
import { P2pMarketTabs } from '@/components/p2p/detail/P2pMarketTabs'
import { BetsCycleLoader } from '@/components/ui/betscycle-loader'
import { useGetP2pMarketDetailQuery } from '@/store/api/p2pApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { resetP2pDetail, selectSelectedP2pOptionId, setSelectedP2pOption } from '@/store/slices/p2pSlice'

function P2pMarketDetailState({ message }: { message: string }) {
	return (
		<PageShell gap='6'>
			<p className='text-sm text-muted-foreground'>{message}</p>
		</PageShell>
	)
}

export default function P2pMarketDetail() {
	const dispatch = useAppDispatch()
	const { marketId } = useParams<{ marketId: string }>()
	const selectedOptionId = useAppSelector(selectSelectedP2pOptionId)

	const { data: detailResponse, isLoading: isDetailLoading, isError: isDetailError } = useGetP2pMarketDetailQuery(marketId ?? '', { skip: !marketId })
	const market = detailResponse?.data

	useEffect(() => {
		if (!marketId) {
			return
		}

		dispatch(resetP2pDetail())

		return () => {
			dispatch(resetP2pDetail())
		}
	}, [dispatch, marketId])

	useEffect(() => {
		const firstOptionId = market?.options[0]?.id

		if (!firstOptionId) {
			return
		}

		dispatch(setSelectedP2pOption(firstOptionId))
	}, [dispatch, market?.id])

	if (!marketId) {
		return <P2pMarketDetailState message='Market not found.' />
	}

	if (isDetailLoading) {
		return (
			<PageShell gap='6'>
				<div className='flex min-h-[50vh] w-full items-center justify-center'>
					<BetsCycleLoader size={40} aria-label='Loading market' />
				</div>
			</PageShell>
		)
	}

	if (isDetailError || !market) {
		return <P2pMarketDetailState message='Unable to load this market right now.' />
	}

	return (
		<PageShell gap='6' footer={<Footer />}>
			<P2pMarketHeader market={market} />

			<div className='grid w-full grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,400px)] lg:items-start'>
				<div className='flex min-w-0 flex-col gap-6'>
					<P2pMarketOutcomes market={market} selectedOptionId={selectedOptionId} />
					<P2pMarketDescription description={market.description} />
					<P2pMarketTabs market={market} />
				</div>

				<div className='min-w-0 lg:sticky lg:top-4'>
					<P2pBuyPanel market={market} />
				</div>
			</div>
		</PageShell>
	)
}
